


import { Component, Vue } from "vue-property-decorator";

import { CustomNotification } from "@Internal/Models";

import { DesignCategories } from "App";
import { Filter, Identifiable, Named } from "App/Contracts";
import { $t, EventBus } from "App/IOC";
import { Column, PagerSorter } from "App/Models";
import { DesignCategory, DesignCategoryLocales, DesignTypes } from "App/Models/DesignCategory";

import { TableAction } from "Web/Contracts";

import RecordsComponent from "Web/Components/Records.vue";
import SimpleSelect from "Web/Components/V2/Select/Simple.vue";

import CategoryForm from "./Form.vue";

/* CATEGORIES DES MODELES — 19/10/2026.

   Les rubriques de la bibliotheque de modeles du logiciel : pages web, sections,
   pop-ups, e-mails, Studio. Une liste filtree par type, rangee dans l'ordre choisi ;
   « Ajouter une catégorie » en tete ; par ligne, « Modifier » et « Supprimer ».
   Le nom se donne par langue ( le francais d'abord ). */
@Component({
    components: {
        CategoryForm,
        Records: RecordsComponent,
        SimpleSelect
    }
})
export default class DesignCategoriesView extends Vue {
    $refs!: Vue[ "$refs" ] & {
        Records: RecordsComponent,
    };

    private records: DesignCategory[] = [];

    private columns: Column[] = [
        Column.Static( "Name", $t( "DESIGN_CATEGORIES_columnName" ) ),
        Column.Static( "TypeId", $t( "DESIGN_CATEGORIES_columnType" ) ),
        Column.Static( "Order", $t( "DESIGN_CATEGORIES_columnOrder" ) ),
        Column.Static( "Translations", $t( "DESIGN_CATEGORIES_columnLocales" ) )
    ];

    private pagerSorter: PagerSorter = new PagerSorter( 500 );

    private actions: TableAction<DesignCategory>[] = [
        {
            Id: "edit",
            ShortName: "edit",
            Name: $t( "DESIGN_CATEGORIES_actionEdit" ),
            Multi: false
        },
        {
            Id: "delete",
            ShortName: "delete",
            Name: $t( "DESIGN_CATEGORIES_actionDelete" ),
            Multi: false
        }
    ];

    /* « Tous les types », puis les cinq types dans l'ordre du volet du logiciel. */
    private typeOptions: ( Identifiable & Named )[] = [
        { Id: 0, Name: $t( "DESIGN_CATEGORIES_allTypes" ) },
        ...DesignTypes.map( type => ( {
            Id: type.Id,
            Name: $t( `DESIGN_CATEGORIES_type_${type.Id}` )
        } ) )
    ];

    private selectedType: Identifiable & Named = this.typeOptions[ 0 ];

    private failed: boolean = false;

    /* La categorie ouverte dans la fenetre : `null` = fermee, `undefined` = nouvelle. */
    private editing: DesignCategory | null | undefined = null;

    private async update(
        _filters: Filter[],
        pagerSorter: PagerSorter
    ): Promise<void> {
        await this.$nextTick();
        this.$refs.Records.Loading();

        const [ result, error ] = await DesignCategories.List( Number( this.selectedType.Id ) );

        const updated = pagerSorter.Copy();
        updated.Total = ( result !== undefined ) ? result.length : 0;

        this.pagerSorter = updated;
        this.failed = ( error !== undefined );
        this.records = ( result !== undefined ) ? result : [];

        if( error !== undefined )
            EventBus.Publish( "Error", error );
    }

    private refresh(): void {
        this.update( [], this.pagerSorter );
    }

    private selectType( option: Identifiable & Named ): void {
        this.selectedType = option;
        this.refresh();
    }

    private executeAction( id: string, rows: DesignCategory[] ): void {
        if( rows.length === 0 )
            return;

        const category = rows[ 0 ];

        switch( id ) {
            case "edit":
                this.editing = category;
                break;

            case "delete":
                EventBus.Publish( "Confirmation", {
                    Props: {
                        Message: this.$t( "DESIGN_CATEGORIES_deleteConfirm", {
                            name: category.Name,
                            type: this.typeLabel( category.TypeId )
                        } ),
                        Actions: [{
                            Id: "delete",
                            Name: this.$t( "DESIGN_CATEGORIES_actionDelete" ),
                            Active: true
                        }]
                    },
                    Events: { Action: () => this.remove( category ) }
                });

                break;
        }
    }

    private async remove( category: DesignCategory ): Promise<void> {
        const error = await DesignCategories.Delete( category.Id );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        EventBus.Publish( "Notification", CustomNotification.Success(
            $t( "DESIGN_CATEGORIES_title" ), $t( "DESIGN_CATEGORIES_deleted" )
        ));

        this.refresh();
    }

    /* Glisser une ligne ( Fred, 19/10/2026 ) : seulement quand un type est choisi, l'ordre se
       range type par type. La liste bouge tout de suite ; l'API suit, puis on relit. */
    private get reorderable(): boolean {
        return Number( this.selectedType.Id ) > 0 && this.records.length > 1;
    }

    private async reorder( move: [ number, number ] ): Promise<void> {
        const [ from, to ] = move;
        const ordered = this.records.slice();
        const [ moved ] = ordered.splice( from, 1 );

        ordered.splice( to, 0, moved );
        this.records = ordered;

        const error = await DesignCategories.Reorder( ordered );

        if( error !== undefined )
            EventBus.Publish( "Error", error );
        else
            EventBus.Publish( "Notification", CustomNotification.Success(
                $t( "DESIGN_CATEGORIES_title" ), $t( "DESIGN_CATEGORIES_reordered" )
            ));

        this.refresh();
    }

    private onSaved(): void {
        this.editing = null;
        this.refresh();
    }

    private typeLabel( typeId: number ): string {
        return DesignTypes.some( type => type.Id === typeId )
            ? this.$t( `DESIGN_CATEGORIES_type_${typeId}` ) as string
            : "—";
    }

    /* Les langues remplies, en capitales : « FR · EN ». */
    private localesOf( category: DesignCategory ): string {
        return DesignCategoryLocales
            .filter( locale => ( category.Translations[ locale ] || "" ) !== "" )
            .map( locale => locale.toUpperCase() )
            .join( " · " );
    }
}
